import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { campaignApi } from '../lib/api';
import { formatCurrency, formatDate, getStatusStyle } from '../lib/utils';
import AppLayout from '../components/AppLayout';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { ArrowLeft, Target, Calendar, Users, Check, Clock, Share2 } from 'lucide-react';

const CampaignDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => { fetchData(); }, [id]);

  const fetchData = async () => {
    try {
      const [campaignRes, progressRes] = await Promise.all([
        campaignApi.getOne(id),
        campaignApi.getProgress(id).catch(() => null),
      ]);
      setCampaign(campaignRes.data);
      setProgress(progressRes?.data);
    } catch (error) {
      toast.error('Failed to load campaign');
      navigate('/earn');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      await campaignApi.accept(id);
      toast.success('Campaign accepted!');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept campaign');
    }
  };

  if (isLoading) {
    return (
      <AppLayout>
        <div className="p-6 lg:p-0 space-y-6">
          <Skeleton className="h-8 w-48" /><Skeleton className="h-48 w-full rounded-xl" /><Skeleton className="h-64 w-full rounded-xl" />
        </div>
      </AppLayout>
    );
  }

  const overallProgress = progress
    ? (progress.total_earned / progress.total_possible) * 100
    : ((campaign.budget_total - campaign.budget_remaining) / campaign.budget_total) * 100;

  const statusStyle = getStatusStyle(campaign.status);

  return (
    <AppLayout>
      <div className="min-h-screen lg:min-h-0 pb-8">
        {/* Header */}
        <div className="p-6 lg:p-0 lg:mb-6 flex items-center gap-4">
          <button onClick={() => navigate(-1)} className="p-2 hover:bg-white/5 rounded-lg transition-colors lg:hidden"><ArrowLeft className="w-6 h-6" /></button>
          <div className="flex-1">
            <h1 className="text-xl lg:text-3xl font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>{campaign.name}</h1>
            <span className={`text-xs px-2 py-1 rounded-full ${statusStyle.bg} ${statusStyle.text}`}>{statusStyle.label}</span>
          </div>
        </div>

        <div className="px-6 lg:px-0 lg:grid lg:grid-cols-3 lg:gap-8 space-y-6 lg:space-y-0">
          {/* Left: Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Campaign Image */}
            {campaign.image_url && (
              <img src={campaign.image_url} alt={campaign.name} className="w-full h-48 lg:h-64 object-cover rounded-xl" />
            )}

            {/* Description */}
            <div>
              <h3 className="font-semibold mb-2">About</h3>
              <p className="text-muted-foreground">{campaign.description}</p>
            </div>

            {/* Milestones */}
            <div>
              <h3 className="font-semibold mb-4">Milestones</h3>
              <div className="space-y-4">
                {(progress?.milestones || campaign.milestones || []).map((milestone, idx) => {
                  const prog = progress?.milestones?.find(m => m.milestone_id === milestone.milestone_id);
                  const current = prog?.current_value || 0;
                  const target = milestone.target_value;
                  const milestoneProgress = (current / target) * 100;
                  const isCompleted = prog?.status === 'completed' || milestoneProgress >= 100;
                  return (
                    <div key={milestone.milestone_id || idx} className="bg-card border border-white/10 rounded-xl p-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${isCompleted ? 'bg-green-500/20' : 'bg-white/10'}`}>
                          {isCompleted ? <Check className="w-5 h-5 text-green-500" /> : <span className="font-medium text-muted-foreground">{idx + 1}</span>}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium mb-1">{milestone.description}</p>
                          <div className="flex items-center gap-2 mb-2">
                            <Progress value={Math.min(milestoneProgress, 100)} className="flex-1 h-2" />
                            <span className="text-sm text-muted-foreground">{current}/{target}</span>
                          </div>
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">Payout</span>
                            <span className={isCompleted ? 'text-green-500' : 'text-primary'}>{formatCurrency(milestone.payout_amount)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Eligibility */}
            {campaign.eligibility_criteria && Object.keys(campaign.eligibility_criteria).length > 0 && (
              <div>
                <h3 className="font-semibold mb-4">Requirements</h3>
                <div className="bg-card border border-white/10 rounded-xl p-4 space-y-3">
                  {campaign.eligibility_criteria.min_followers && (
                    <div className="flex items-center gap-3"><Users className="w-5 h-5 text-muted-foreground" /><span>Minimum {campaign.eligibility_criteria.min_followers.toLocaleString()} followers</span></div>
                  )}
                  {campaign.eligibility_criteria.min_kyc_tier && (
                    <div className="flex items-center gap-3"><Check className="w-5 h-5 text-muted-foreground" /><span>KYC Tier {campaign.eligibility_criteria.min_kyc_tier}+ required</span></div>
                  )}
                  {campaign.eligibility_criteria.countries && (
                    <div className="flex items-center gap-3"><Target className="w-5 h-5 text-muted-foreground" /><span>Available in: {campaign.eligibility_criteria.countries.join(', ')}</span></div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Right: Earnings Sidebar */}
          <div className="lg:col-span-1 space-y-6">
            {/* Earnings Card */}
            <div className="bg-card border border-white/10 rounded-2xl p-6 card-accent relative overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-primary" />
              <div className="text-center mb-6">
                <p className="text-muted-foreground mb-1">Earned</p>
                <p className="text-3xl font-bold balance-text text-green-500">{formatCurrency(progress?.total_earned || (campaign.budget_total - campaign.budget_remaining))}</p>
                <p className="text-sm text-muted-foreground mt-1">of {formatCurrency(progress?.total_possible || campaign.budget_total)} possible</p>
              </div>
              <div className="flex items-center justify-center mb-4">
                <div className="relative w-28 h-28">
                  <svg className="w-full h-full transform -rotate-90">
                    <circle cx="56" cy="56" r="48" stroke="currentColor" strokeWidth="8" fill="none" className="text-white/10" />
                    <circle cx="56" cy="56" r="48" stroke="currentColor" strokeWidth="8" fill="none" strokeDasharray={`${2 * Math.PI * 48}`} strokeDashoffset={`${2 * Math.PI * 48 * (1 - overallProgress / 100)}`} className="text-primary transition-all duration-500" strokeLinecap="round" />
                  </svg>
                  <div className="absolute inset-0 flex items-center justify-center"><span className="text-xl font-bold">{overallProgress.toFixed(0)}%</span></div>
                </div>
              </div>
            </div>

            {/* Campaign Info */}
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <Target className="w-5 h-5 text-primary mb-2" />
                <p className="text-sm text-muted-foreground">Objective</p>
                <p className="font-medium capitalize text-sm">{campaign.objective?.replace('_', ' ')}</p>
              </div>
              <div className="bg-card border border-white/10 rounded-xl p-4">
                <Calendar className="w-5 h-5 text-primary mb-2" />
                <p className="text-sm text-muted-foreground">Duration</p>
                <p className="font-medium text-sm">{formatDate(campaign.start_date, { month: 'short', day: 'numeric' })} - {formatDate(campaign.end_date, { month: 'short', day: 'numeric' })}</p>
              </div>
            </div>

            {/* Actions */}
            <div className="space-y-3">
              {!progress && campaign.status === 'active' && (
                <Button onClick={handleAccept} className="w-full h-12 bg-primary hover:bg-red-600 text-white font-semibold glow-primary" data-testid="accept-campaign">Join Campaign</Button>
              )}
              <Button variant="outline" className="w-full h-12" onClick={() => navigator.share?.({ title: campaign.name, text: `Check out this campaign: ${campaign.name}`, url: window.location.href })}>
                <Share2 className="w-5 h-5 mr-2" />Share Campaign
              </Button>
            </div>
          </div>
        </div>
      </div>
    </AppLayout>
  );
};

export default CampaignDetailPage;
